/**
 * Groups admin API (ADR-027).
 *
 * main-service mounts this router at the API root (`/team-groups/...`, the current routing convention), not
 * under `/v1`, so every call overrides the axios baseURL the same way the Box Truck client does. The global
 * auth-token interceptor still applies.
 *
 * Only `TEAM_SETTINGS` holders can call these, with three deliberate exceptions — the driver roster, the
 * caller's OWN managed groups, and the Box Truck driver picker for the HR who onboards drivers. Otherwise the
 * group list and its membership are admin-only (ADR-027 §8). The badge on a driver row comes from
 * `/driver-badges/v1`, which every dispatcher may call: it names only the group of a driver they can see.
 */
import axios from 'axios'
import { ClientUser } from '@numeo/types-core'
import {
    TeamGroupDriverBadgesDto,
    TeamGroupDriverOptionsDto,
    TeamGroupDto,
    TeamGroupMemberOption,
    TeamGroupMembersInput,
    TeamGroupSummary,
} from '@numeo/types-main-web-app'
import { getApiBaseUrlWithoutV1 } from '@/setup/SetupAxios'

const BASE = getApiBaseUrlWithoutV1()

interface ApiEnvelope<T> {
    success: boolean
    data: T
    message?: string
}

export const fetchTeamGroups = async (): Promise<TeamGroupDto[]> => {
    const res = await axios.get<ApiEnvelope<TeamGroupDto[]>>('/team-groups/v1', { baseURL: BASE })
    return res.data?.data ?? []
}

/**
 * The group badge of each of these drivers, keyed by fleet driver record id — for the Box Truck driver lists.
 *
 * Open to every dispatcher: the server answers only for drivers the caller may already see, with the group's
 * name and colour and no membership. A driver in no group is simply absent.
 *
 * `driverIds` omitted asks for every driver the caller may see, in ONE request. For a list of a thousand
 * conversations that is the only affordable shape: the ids would not fit a query string, and chunking them
 * pays the server's group reads once per chunk for an answer it computes in memory either way.
 */
export const fetchDriverTeamGroupBadges = async (driverIds?: string[]): Promise<Record<string, TeamGroupSummary>> => {
    const res = await axios.get<ApiEnvelope<TeamGroupDriverBadgesDto>>('/team-groups/driver-badges/v1', {
        baseURL: BASE,
        params: driverIds ? { driverIds: driverIds.join(',') } : undefined,
    })
    return res.data?.data?.badges ?? {}
}

/** The Groups picker on a Box Truck driver for Box Truck intake: names and colours, and that driver's own groups. */
export const fetchTeamGroupDriverOptions = async (driverId?: string): Promise<TeamGroupDriverOptionsDto> => {
    const res = await axios.get<ApiEnvelope<TeamGroupDriverOptionsDto>>('/team-groups/driver-options/v1', {
        baseURL: BASE,
        params: driverId ? { driverId } : undefined,
    })
    return res.data?.data ?? { groups: [], driverGroupIds: [] }
}

export const createTeamGroup = async (name: string, color?: string | null): Promise<TeamGroupDto> => {
    const res = await axios.post<ApiEnvelope<TeamGroupDto>>('/team-groups/v1', { name, color }, { baseURL: BASE })
    return res.data.data
}

export const deleteTeamGroup = async (groupId: string): Promise<void> => {
    await axios.delete(`/team-groups/${groupId}/v1`, { baseURL: BASE })
}

/**
 * Replace membership for the kinds present in `members`. `expectedUpdatedAt` is the version the editor
 * loaded: when another admin saved in the meantime the server answers 409 rather than letting this Save
 * silently overwrite their whole member set.
 */
export const saveTeamGroupMembers = async (
    groupId: string,
    members: TeamGroupMembersInput,
    /** Omitted by the manager's editor: promotion is an admin act, and the server ignores the field from a
     *  manager anyway. Sending nothing says so rather than sending a list that will be dropped. */
    managers: string[] | undefined,
    expectedUpdatedAt?: string
): Promise<TeamGroupDto> => {
    const res = await axios.put<ApiEnvelope<TeamGroupDto>>(`/team-groups/${groupId}/members/v1`, { members, managers, expectedUpdatedAt }, { baseURL: BASE })
    return res.data.data
}

/**
 * The groups the CURRENT user manages — empty for everyone who is not a Dispatch Manager.
 *
 * The one group-shaped thing a non-admin may read (ADR-027 §8), and it carries names only, never membership.
 * A failure THROWS rather than answering `[]`: an empty list means "not a manager", and a network blip must
 * not silently demote someone out of their own screens.
 */
export const fetchManagedTeamGroups = async (): Promise<TeamGroupSummary[]> => {
    const res = await axios.get<ApiEnvelope<TeamGroupSummary[]>>('/team-groups/managed/v1', { baseURL: BASE })
    return res.data.data ?? []
}

/**
 * The driver rows the current user may work with (ADR-027 surface 9).
 *
 * The SERVER builds this list. Profiles → Drivers used to render the application payload and hide rows
 * locally, which is presentation, not authorization — so the roster now comes from an endpoint that returns
 * only the allowed records. Unlike the rest of this module it needs no admin permission: it returns no group
 * name and no membership.
 *
 * Rows are the same `ClientUser` shape the application payload carries, so the table is unchanged.
 */
export const fetchDriverRoster = async (): Promise<ClientUser[]> => {
    const res = await axios.get<ApiEnvelope<{ drivers: ClientUser[] }>>('/team-groups/driver-roster/v1', { baseURL: BASE })
    return res.data?.data?.drivers ?? []
}

/**
 * Who may be assigned alongside these drivers (ADR-027)?
 *
 * A truck carries drivers and dispatchers, and a dispatcher works a driver only if they share a desk.
 * `unrestricted` means nothing constrains the choice — no groups exist, or these drivers belong to none.
 * Like {@link fetchVisibleDriverScope} this needs no admin permission: it returns ids the caller can
 * already read from the Team list and names no group.
 */
export interface AssignableUserScope {
    unrestricted: boolean
    userIds?: string[]
}

export const fetchAssignableUserScope = async (driverIds: string[]): Promise<AssignableUserScope> => {
    if (driverIds.length === 0) return { unrestricted: true }
    const res = await axios.get<ApiEnvelope<AssignableUserScope>>('/team-groups/assignable-users/v1', {
        baseURL: BASE,
        params: { driverIds: driverIds.join(',') },
    })
    return res.data?.data ?? { unrestricted: true }
}

/**
 * Everything a Dispatch Manager's editor renders: the desks they run, and the people they may put on them.
 *
 * The candidate lists come from the SERVER because they are the authorization rule — the same predicate the
 * write is judged by — so deriving them here would be a second copy that can disagree. Another desk's members
 * are absent from the answer entirely, which is what keeps group membership admin-only (ADR-027 §8) even
 * though a non-admin opens this page.
 *
 * `groups: []` means the caller manages nothing, and the page is not theirs to draw.
 */
interface ManagedGroupsEditor {
    groups: TeamGroupDto[]
    /** Four fields per person, not `ClientUser`: this endpoint is open to a non-admin, so the server projects
     *  only what the picker renders. Typed from the shared definition so the two cannot drift. */
    users: TeamGroupMemberOption[]
    drivers: TeamGroupMemberOption[]
}

export const fetchManagedGroupsEditor = async (): Promise<ManagedGroupsEditor> => {
    const res = await axios.get<ApiEnvelope<ManagedGroupsEditor>>('/team-groups/managed/editor/v1', { baseURL: BASE })
    return res.data?.data ?? { groups: [], users: [], drivers: [] }
}
